"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TimeRangePicker = void 0;
var tslib_1 = require("tslib");
var react_1 = tslib_1.__importStar(require("react"));
var classnames_1 = tslib_1.__importDefault(require("classnames"));
var moment_1 = tslib_1.__importDefault(require("moment"));
var controlled_1 = require("../form/controlled");
var CalendarPart_1 = tslib_1.__importDefault(require("../calendar/CalendarPart"));
var CalendarTable_1 = require("../calendar/CalendarTable");
var Input_1 = require("../input/Input");
var i18n_1 = require("../i18n");
var util_1 = require("./util");
var dropdown_1 = require("../dropdown");
var button_1 = require("../button");
var Popover_1 = require("../popover/Popover");
var util_2 = require("../datepicker/util");
var use_default_1 = require("../_util/use-default");
var config_context_1 = require("../_util/config-context");
var forward_ref_with_statics_1 = require("../_util/forward-ref-with-statics");
var merge_event_props_1 = require("../_util/merge-event-props");
var key_map_1 = require("../_util/key-map");
var getDefaultMoment = function (_a) {
    var range = _a.range, timeDisabled = _a.timeDisabled, _b = _a.hourStep, hourStep = _b === void 0 ? 1 : _b, _c = _a.minuteStep, minuteStep = _c === void 0 ? 1 : _c, _d = _a.secondStep, secondStep = _d === void 0 ? 1 : _d;
    return util_1.getValidTimeValue(moment_1.default("00:00:00", "HH:mm:ss"), tslib_1.__assign({ range: range,
        hourStep: hourStep,
        minuteStep: minuteStep,
        secondStep: secondStep }, timeDisabled));
};
function isValidRangeMomentValue(value) {
    return (Array.isArray(value) &&
        moment_1.default.isMoment(value[0]) &&
        moment_1.default.isMoment(value[1]));
}
exports.TimeRangePicker = forward_ref_with_statics_1.forwardRefWithStatics(function TimeRangePicker(props, ref) {
    var classPrefix = config_context_1.useConfig().classPrefix;
    var t = i18n_1.useTranslation(moment_1.default);
    var _a = controlled_1.useDefaultValue(props, [null, null]), className = _a.className, style = _a.style, header = _a.header, value = _a.value, onChange = _a.onChange, disabled = _a.disabled, _b = _a.separator, separator = _b === void 0 ? "~" : _b, _c = _a.format, format = _c === void 0 ? "HH:mm:ss" : _c, _d = _a.placeholder, placeholder = _d === void 0 ? t.selectTime : _d, _e = _a.defaultOpen, defaultOpen = _e === void 0 ? false : _e, open = _a.open, _f = _a.onOpenChange, onOpenChange = _f === void 0 ? function () { return null; } : _f, _g = _a.placement, placement = _g === void 0 ? "bottom-start" : _g, _h = _a.placementOffset, placementOffset = _h === void 0 ? 5 : _h, _j = _a.closeOnScroll, closeOnScroll = _j === void 0 ? true : _j, escapeWithReference = _a.escapeWithReference, popupContainer = _a.popupContainer, overlayClassName = _a.overlayClassName, overlayStyle = _a.overlayStyle, range = _a.range, _k = _a.disabledTime, disabledTime = _k === void 0 ? function () { return ({}); } : _k, _l = _a.hourStep, hourStep = _l === void 0 ? 1 : _l, _m = _a.minuteStep, minuteStep = _m === void 0 ? 1 : _m, _o = _a.secondStep, secondStep = _o === void 0 ? 1 : _o, disableAutoAdjust = _a.disableAutoAdjust, _p = _a.caption, caption = _p === void 0 ? [t.startTime, t.endTime] : _p;
    // 当前选中时间
    var _q = tslib_1.__read(react_1.useState(isValidRangeMomentValue(value)
        ? [value[0].clone(), value[1].clone()]
        : [null, null]), 2), curValue = _q[0], setCurValue = _q[1];
    // 选择器是否展开
    var _r = tslib_1.__read(use_default_1.useDefault(open, defaultOpen, onOpenChange), 2), active = _r[0], setActive = _r[1];
    // 输入框显示值
    var getInputValue = react_1.useCallback(function (value) {
        var _a = tslib_1.__read(value || [null, null], 2), start = _a[0], end = _a[1];
        if (moment_1.default.isMoment(start) && moment_1.default.isMoment(end)) {
            return start
                .locale(t.locale)
                .format(format) + " " + separator + " " + end
                .locale(t.locale)
                .format(format);
        }
        return "";
    }, [format, separator, t.locale]);
    var _s = tslib_1.__read(react_1.useState(getInputValue(curValue)), 2), inputValue = _s[0], setInputValue = _s[1];
    react_1.useEffect(function () {
        var _a = tslib_1.__read(value, 2), start = _a[0], end = _a[1];
        var curStart = moment_1.default.isMoment(start)
            ? start.clone()
            : getDefaultMoment({
                range: range,
                hourStep: hourStep,
                minuteStep: minuteStep,
                secondStep: secondStep,
                timeDisabled: disabledTime(value, "start"),
            });
        var curEnd = moment_1.default.isMoment(end)
            ? end.clone()
            : getDefaultMoment({
                range: range,
                hourStep: hourStep,
                minuteStep: minuteStep,
                secondStep: secondStep,
                timeDisabled: disabledTime([curStart, end], "end"),
            });
        setCurValue([curStart, curEnd]);
        setInputValue(getInputValue(value));
    }, [format, separator, value]); // eslint-disable-line react-hooks/exhaustive-deps
    var timerRef = react_1.useRef(null);
    react_1.useEffect(function () { return function () {
        clearTimeout(timerRef.current);
    }; }, []);
    function handleInputChange(content) {
        setInputValue(content);
        // 格式校验
        var _a = tslib_1.__read(content.split(separator).map(function (i) { return i.trim(); }), 2), startStr = _a[0], endStr = _a[1];
        var value = [
            moment_1.default(startStr, format, true),
            moment_1.default(endStr, format, true),
        ];
        if (!value[0].isValid() || !value[1].isValid()) {
            return;
        }
        var startValid = util_1.isValidTimeValue(value[0], tslib_1.__assign({ format: format,
            hourStep: hourStep,
            minuteStep: minuteStep,
            secondStep: secondStep,
            range: range }, disabledTime(value, "start")));
        var endValid = util_1.isValidTimeValue(value[1], tslib_1.__assign({ format: format,
            hourStep: hourStep,
            minuteStep: minuteStep,
            secondStep: secondStep,
            range: range }, disabledTime(value, "end")));
        if (startValid && endValid) {
            setCurValue(value);
        }
    }
    function handleKeyDown(event) {
        if (!active) {
            handleOpen();
            return;
        }
        switch (event.key) {
            case key_map_1.KeyMap.Enter:
                handleOk(event);
                break;
            case key_map_1.KeyMap.Esc:
                handleClose();
                break;
        }
    }
    function handleChange(partial, value
    // context: DateChangeContext
    ) {
        var _a = tslib_1.__read(value, 2), start = _a[0], end = _a[1];
        var fullValue = [
            start ||
                getDefaultMoment({
                    range: range,
                    hourStep: hourStep,
                    minuteStep: minuteStep,
                    secondStep: secondStep,
                    timeDisabled: disabledTime(value, "start"),
                }),
            end ||
                getDefaultMoment({
                    range: range,
                    hourStep: hourStep,
                    minuteStep: minuteStep,
                    secondStep: secondStep,
                    timeDisabled: disabledTime(value, "end"),
                }),
        ];
        // 调整至合法时间
        if (partial === "start") {
            fullValue[1] = util_1.getValidTimeValue(fullValue[1], tslib_1.__assign({ range: range,
                hourStep: hourStep,
                minuteStep: minuteStep,
                secondStep: secondStep }, disabledTime(fullValue, "end")));
        }
        else {
            fullValue[0] = util_1.getValidTimeValue(fullValue[0], tslib_1.__assign({ range: range,
                hourStep: hourStep,
                minuteStep: minuteStep,
                secondStep: secondStep }, disabledTime(fullValue, "start")));
        }
        setCurValue(fullValue);
        // [TODO] moment 更改后直接获取值（format）可能拿到是之前值？
        timerRef.current = setTimeout(function () { return setInputValue(getInputValue(fullValue)); }, 0);
    }
    function handleOk(event) {
        var value = curValue;
        if (!disableAutoAdjust &&
            isValidRangeMomentValue(curValue) &&
            util_2.isAfter(curValue[0], curValue[1])) {
            value = [curValue[1], curValue[0]];
            setCurValue(value);
        }
        onChange(value, { event: event });
        handleClose();
    }
    function handleOpen() {
        if (disabled) {
            return;
        }
        setActive(true);
    }
    function handleClose() {
        setInputValue(getInputValue(value));
        setActive(false);
    }
    var timeProps = {
        hourStep: hourStep,
        minuteStep: minuteStep,
        secondStep: secondStep,
        format: format,
    };
    return (react_1.default.createElement(Popover_1.Popover, { trigger: [
            util_2.DatePickerTrigger,
            { onOpen: handleOpen, onClose: handleClose },
        ], visible: active, onVisibleChange: setActive, placement: placement, placementOffset: placementOffset, closeOnScroll: closeOnScroll, escapeWithReference: escapeWithReference, popupContainer: popupContainer, overlayClassName: overlayClassName, overlayStyle: overlayStyle, overlay: react_1.default.createElement(dropdown_1.DropdownBox, { preventMouseDown: true },
            react_1.default.createElement(CalendarPart_1.default.Panel, { rangeMode: true, timeMode: true },
                !!header && react_1.default.createElement(CalendarPart_1.default.Header, null, header),
                react_1.default.createElement(CalendarPart_1.default.Body, null,
                    react_1.default.createElement(CalendarTable_1.CalendarTable, tslib_1.__assign({}, props, { rangeType: "start", type: "time", showTime: tslib_1.__assign(tslib_1.__assign({}, timeProps), { caption: Array.isArray(caption) ? caption[0] : null }), value: curValue, onChange: function (value) {
                            return handleChange("start", value);
                        } })),
                    react_1.default.createElement(CalendarTable_1.CalendarTable, tslib_1.__assign({}, props, { rangeType: "end", type: "time", showTime: tslib_1.__assign(tslib_1.__assign({}, timeProps), { caption: Array.isArray(caption) ? caption[1] : null }), value: curValue, onChange: function (value) {
                            return handleChange("end", value);
                        } }))),
                react_1.default.createElement(CalendarPart_1.default.Footer, { right: react_1.default.createElement(button_1.Button, { type: "primary", htmlType: "button", onClick: handleOk }, t.okText) }))) },
        react_1.default.createElement("div", tslib_1.__assign({ ref: ref, className: classnames_1.default(classPrefix + "-timepicker", className), style: style }, merge_event_props_1.mergeEventProps(props)),
            react_1.default.createElement("div", { className: classPrefix + "-timepicker__input size-l" },
                react_1.default.createElement(Input_1.Input, { maxLength: 19, disabled: disabled, placeholder: placeholder, value: inputValue, onChange: handleInputChange, onKeyDown: handleKeyDown, onClick: function (event) {
                        if (active) {
                            event.stopPropagation();
                        }
                    } })))));
}, { defaultLabelAlign: "middle" });
exports.TimeRangePicker.displayName = "TimeRangePicker";
//# sourceMappingURL=TimeRangePicker.js.map